import { useState } from 'react';
import { Button } from '@rudi/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@rudi/ui/select';
import { toast } from 'sonner';
import { ChatModelFields } from '../components/ChatModelFields';
import { useUpdateTenantConfig } from '../api/hooks';
import { CHAT_BACKEND_LABELS, CHAT_BACKEND_ORDER } from '../api/chat-defaults';
import type { AdminTenant, AIChatBackend } from '../api/types';
const FOLLOW_PLATFORM_DEFAULT = 'default';

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; detail?: string } } })?.response
    ?.data;
  return data?.error ?? data?.detail ?? (error instanceof Error ? error.message : 'Update failed');
}

export function ChatExecutorSection({ tenant }: { tenant: AdminTenant }) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateTenantConfig(tenant.id);
  const modelConfig = tenant.ai_chat_model;
  const modelOptions = tenant.ai_chat_model_options;

  // A missing source means an older backend that does not report one; treat it
  // as a pin so a rolling deploy never claims "follows the default" wrongly.
  const source = tenant.ai_chat_backend_source ?? 'tenant';
  const value = source === 'default' ? FOLLOW_PLATFORM_DEFAULT : tenant.ai_chat_backend;
  const platformDefault = tenant.platform_ai_chat_backend;
  const defaultLabel = platformDefault
    ? `Platform default (${CHAT_BACKEND_LABELS[platformDefault]})`
    : 'Platform default';

  const save = async (next: string) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        ai_chat_backend: next === FOLLOW_PLATFORM_DEFAULT ? null : (next as AIChatBackend),
      });
      toast.success('Chat executor updated');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const saveModel = async (model: string, effort: string) => {
    setError(null);
    try {
      await mutation.mutateAsync({ ai_chat_model: { model, effort } });
      toast.success('Chat model updated');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const resetModel = async () => {
    setError(null);
    try {
      await mutation.mutateAsync({ ai_chat_model: null });
      toast.success('Chat model reset to default');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <section className="space-y-3 rounded-3xl border p-4">
      <div>
        <h2 className="text-sm font-medium">Chat defaults</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Choose the executor, model, and reasoning level for new chats in this tenant. Existing
          chats keep their settings.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Select value={value} disabled={mutation.isPending} onValueChange={v => void save(v)}>
        <SelectTrigger className="w-64" aria-label="Chat executor">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FOLLOW_PLATFORM_DEFAULT}>{defaultLabel}</SelectItem>
          {value === 'claude_code' && (
            <SelectItem value="claude_code" disabled>
              Claude Code CLI retired
            </SelectItem>
          )}
          {CHAT_BACKEND_ORDER.map(backend => (
            <SelectItem key={backend} value={backend}>
              {CHAT_BACKEND_LABELS[backend]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {source === 'default'
          ? 'Using the platform executor default.'
          : 'Executor override saved for this tenant.'}
      </p>
      {(!modelConfig || !modelOptions) && (
        <p className="text-sm text-muted-foreground">
          This Rudi connection does not expose model defaults. You can still change the executor.
        </p>
      )}
      {modelConfig && modelOptions && (
        <div className="space-y-3">
          <ChatModelFields
            model={modelConfig.model}
            effort={modelConfig.effort}
            options={modelOptions}
            disabled={mutation.isPending}
            onChange={(model, effort) => void saveModel(model, effort)}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {modelConfig.source === 'tenant'
                ? 'Saved for this tenant and executor.'
                : 'Using the platform model default for this executor.'}
            </p>
            <Button
              size="sm"
              variant="outline"
              disabled={mutation.isPending || modelConfig.source === 'default'}
              onClick={() => void resetModel()}
            >
              Use platform model
            </Button>
          </div>
          {tenant.ai_chat_backend === 'api' && (
            <p className="text-xs text-muted-foreground">
              OpenAI models use the tenant's OpenAI API key, which must have access to the selected
              model. OpenRouter models use Rudi's OpenRouter key, or an API key named openrouter.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
